import { ReactElement, useRef, useState } from "react";
import Sidebar from "../../components/Sidebar/Sidebar";
import clsx from "clsx";
import userPreferences from "../../store/userPreferences";
import MediaQuery from "react-responsive";
import MobileNavigation from "../../components/Navigations/MobileNavigation";
import OfflineNotificationBar from "../../components/OfflineNotificationBar/OfflineNotificationBar";
import useOnlineStatus from "../../hooks/useOnlineStatus";
import { AnimatePresence, motion } from "framer-motion";
import MobileTopNavigation from "../../Logo/MobileTopNavigation";
import useMobile from "../../hooks/useMobile";
// import BackgroundChecker from "../../components/BackgroundChecker/BackgroundChecker";

interface ITwoSidebars {
  children: ReactElement;
  right?: ReactElement | string | null;
  rightClasses?: string;
  isLoading?: boolean;
  mobileMenuChildren?: ReactElement | null;
}

const TwoSidebars: React.FC<ITwoSidebars> = ({
  children,
  right = null,
  rightClasses = "",
  isLoading = false,
  mobileMenuChildren,
}) => {
  const { rightPanelIsVisible } = userPreferences();
  const isOnline = useOnlineStatus();
  const { isMobile } = useMobile();
  const mobileTopNavigationRef = useRef(null);
  const [rightPanelAnimationComplete, setRightPanelAnimationComplete] =
    useState<boolean>(false);

  return (
    <>
      {/* <UpdateAvailableBanner /> */}
      {!isOnline && <OfflineNotificationBar />}
      {isMobile && <MobileTopNavigation ref={mobileTopNavigationRef} />}

      <div
        className={clsx([
          "relative",
          "flex",
          "flex-col",
          "md:flex-row",
          "md:h-[100vh]",
          {
            "h-[100vh]": isLoading,
            "pb-[80px]": mobileMenuChildren,
            "pb-[60px]": !mobileMenuChildren,
            "pt-[60px]": isLoading,
            "pt-[120px]": !isLoading,
          },
          "md:pb-0",
          "md:pt-0",
          "z-auto",
        ])}
      >
        {!isMobile && (
          <div className={clsx(["flex", "flex-shrink-0", "md:z-0"])}>
            <Sidebar />
          </div>
        )}

        <div
          className={clsx([
            "flex",
            "p-4",
            "h-full",
            "md:overflow-y-scroll",
            "md:overflow-x-hidden",
            "flex-col",
            "flex-grow",
            "flex-shrink",
            "no-scrollbar",
            "pt-[30px]",
            "md:pt-0",
            "mb-8",
          ])}
          id="content-wrapper-parent"
        >
          <div
            className={clsx([
              "md:pt-0",
              "pb-0",
              "min-h-full",
              "flex",
              "flex-col",
              "flex-grow",
              // {
              //   "md:pb-5": !isLoading,
              // },
            ])}
            id="content-wrapper"
          >
            <motion.div
              className={clsx(["flex", "flex-grow", "z-0", "md:z-auto"])}
              {...(isMobile
                ? {}
                : {
                    initial: {
                      opacity: 0.2,
                      transition: { duration: 1 },
                    },
                    animate: {
                      opacity: 1,
                      transition: { duration: 1 },
                    },
                    exit: {
                      opacity: 0.2,
                      transition: { duration: 1 },
                    },
                  })}
            >
              {children}
            </motion.div>
            {!isLoading && (
              <div className={clsx(["md:min-h-[30px]", "flex"])} />
            )}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {!isLoading && right ? (
            <>
              <MediaQuery maxWidth={767}>
                <div
                  className={clsx([
                    "flex",
                    "flex-col",
                    "md:w-[350px]",
                    "md:overflow-auto",
                    "p-4",
                    "pb-10 md:pb-4",
                    "pt-0 md:pt-4",
                    {
                      "md:bg-gray-100": true,
                    },
                    "flex-shrink-0",
                    "gap-6",
                    "md:gap-6",
                    // "z-10",
                    rightClasses,
                  ])}
                >
                  {right}
                </div>
              </MediaQuery>
              <MediaQuery minWidth={767}>
                <motion.div
                  initial={{
                    width: rightPanelIsVisible ? 350 : 0,
                    opacity: rightPanelIsVisible ? 1 : 0,
                  }}
                  animate={{
                    width: rightPanelIsVisible ? 350 : 0,
                    opacity: rightPanelIsVisible ? 1 : 0,
                  }}
                  transition={{
                    ease: "easeInOut",
                    type: "spring",
                    stiffness: 400,
                    damping: 17,
                    duration: 0.3,
                  }}
                  onAnimationComplete={() =>
                    setRightPanelAnimationComplete(true)
                  }
                  className={clsx([
                    "flex",
                    "overflow-auto",
                    "no-scrollbar",
                    "md:py-4",
                    "flex-shrink-0",
                    "!shadow-[rgba(0,0,0,0.1)_-2px_0_10px]",
                    "dark:!shadow-[rgba(0,0,0,0.2)_-2px_0_10px]",
                    "overflow-x-hidden",
                    "md:dark:bg-gray-800",
                    "md:bg-sky-50",
                    "z-0",
                  ])}
                >
                  <AnimatePresence mode="wait">
                    {rightPanelAnimationComplete && (
                      <div
                        className={clsx([
                          "px-4",
                          "gap-7",
                          "flex",
                          "flex-col",
                          "after:block after:h-0 after:pb-4 after:content-['']",
                          "w-full",
                        ])}
                      >
                        {rightPanelIsVisible && right}
                      </div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </MediaQuery>
            </>
          ) : null}
        </AnimatePresence>
      </div>
      <>
        {mobileMenuChildren ? (
          <MobileNavigation>{mobileMenuChildren || null}</MobileNavigation>
        ) : (
          <MobileNavigation />
        )}
      </>
    </>
  );
};

export default TwoSidebars;
